import React, { useState, useEffect } from 'react';
import {
  TextField,
  Button,
  Typography,
  Box,
  Alert,
  Paper,
  Avatar,
  Fade,
  Zoom,
  Slide,
  IconButton,
  useTheme
} from '@mui/material';
import {
  Person,
  Email,
  VpnKey,
  Google,
  DarkMode,
  LightMode
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { useThemeMode } from '../contexts/ThemeContext';
import { Link, useNavigate } from 'react-router-dom';
import LynaAvatar from '../components/LynaAvatar';
import '../cat-theme.css';

// Declare Google global
declare global {
  interface Window {
    google: any;
  }
}

const Register: React.FC = () => {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const { register, googleLogin } = useAuth();
  const navigate = useNavigate();
  const theme = useTheme();
  const { mode, variant, toggleTheme } = useThemeMode();
  const googleButtonRef = React.useRef<HTMLDivElement>(null);
  const isDark = mode === 'dark';
  const isBlack = variant === 'pure-black' && isDark;
  const isWhite = variant === 'pure-white' && !isDark;
  const isTrippy = variant === 'trippy';

  // Dynamic colors based on theme and variant
  const getBackground = () => {
    if (isTrippy) return isDark ? 'linear-gradient(135deg, #0D0221 0%, #1A0836 100%)' : 'linear-gradient(135deg, #FF00FF 0%, #00FFCC 100%)';
    if (isBlack) return '#000000';
    if (isWhite) return '#FFFFFF';
    return isDark ? 'linear-gradient(135deg, #1A1A1A 0%, #0A0A0A 100%)' : 'linear-gradient(135deg, #E8D5F2 0%, #FFF5F8 100%)';
  };

  const cardBg = isTrippy 
    ? (isDark ? 'rgba(26, 8, 54, 0.8)' : 'rgba(255, 255, 255, 0.8)')
    : (isBlack ? 'rgba(10, 10, 10, 0.9)' : (isDark ? 'rgba(30,30,30,0.95)' : 'rgba(255,255,255,0.9)'));
    
  const cardBorder = isBlack ? 'rgba(255,255,255,0.05)' : 'rgba(255,255,255,0.2)';
  const textPrimary = isDark ? '#FFFFFF' : '#2D3436';
  const taglineColor = isTrippy ? (isDark ? '#00FFCC' : '#FF00FF') : (isDark ? '#FFB3C9' : '#FF6B9D');
  const linkColor = taglineColor;
  const linkHoverColor = isTrippy ? (isDark ? '#FF00FF' : '#00FFCC') : '#9B59B6';

  useEffect(() => {
    const timer = setTimeout(() => setShowForm(true), 500);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    const initializeGoogle = () => {
      if (window.google && process.env.REACT_APP_GOOGLE_CLIENT_ID && googleButtonRef.current) {
        try {
          window.google.accounts.id.initialize({
            client_id: process.env.REACT_APP_GOOGLE_CLIENT_ID,
            callback: handleGoogleResponse,
            ux_mode: 'popup',
            auto_select: false,
            use_fedcm_for_prompt: false
          });
          window.google.accounts.id.renderButton(
            googleButtonRef.current,
            { theme: 'outline', size: 'large', width: '100%', shape: 'pill', text: 'signup_with' }
          );
          return true;
        } catch (err) {
          console.error('Error initializing Google Sign-In:', err);
          return false;
        }
      }
      return false;
    };

    if (!initializeGoogle()) {
      const intervalId = setInterval(() => {
        if (initializeGoogle()) clearInterval(intervalId);
      }, 100);
      const timeoutId = setTimeout(() => clearInterval(intervalId), 5000);
      return () => {
        clearInterval(intervalId);
        clearTimeout(timeoutId);
      };
    }
  }, []);

  const handleGoogleResponse = async (response: any) => {
    setIsLoading(true);
    try {
      await googleLogin(response.credential);
      navigate('/dashboard');
    } catch (err: any) {
      setError(err.response?.data?.message || 'Google login failed');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      await register(email, password, name);
      navigate('/dashboard');
    } catch (err: any) {
      setError(err.response?.data?.message || 'Registration failed');
      setIsLoading(false);
    }
  };

  return (
    <Box
      className={isTrippy ? 'theme-trippy cat-paw-background' : 'cat-paw-background'}
      sx={{
        minHeight: '100vh',
        display: 'flex',
        position: 'relative',
        overflow: 'hidden',
        background: getBackground(),
        transition: 'all 0.5s ease',
      }}
    >
      <IconButton 
        onClick={toggleTheme} 
        sx={{ 
          position: 'absolute', 
          top: 16, 
          right: 16, 
          zIndex: 10, 
          color: isDark ? '#FFD700' : '#9B59B6', 
          bgcolor: isDark ? 'rgba(255,255,255,0.1)' : 'rgba(155,89,182,0.1)',
          backdropFilter: 'blur(10px)',
          '&:hover': { bgcolor: isDark ? 'rgba(255,255,255,0.2)' : 'rgba(155,89,182,0.2)' }
        }}
      >
        {isDark ? <LightMode /> : <DarkMode />}
      </IconButton>

      {/* Floating Elements (Subtle) */}
      <Box sx={{ position: 'absolute', top: '15%', left: '8%', animation: 'float 7s ease-in-out infinite', fontSize: '4rem', opacity: isDark ? 0.3 : 0.6 }}>🧶</Box>
      <Box sx={{ position: 'absolute', top: '25%', right: '12%', animation: 'float 9s ease-in-out infinite reverse', fontSize: '3.5rem', opacity: isDark ? 0.3 : 0.6 }}>🥛</Box>
      <Box sx={{ position: 'absolute', bottom: '10%', left: '15%', animation: 'float 8s ease-in-out infinite', fontSize: '5rem', opacity: isDark ? 0.2 : 0.4 }}>🐾</Box>
      <Box sx={{ position: 'absolute', bottom: '25%', right: '8%', animation: 'float 10s ease-in-out infinite reverse', fontSize: '4rem', opacity: isDark ? 0.3 : 0.6 }}>🐈</Box>

      <Box sx={{ width: '100%', maxWidth: 480, mx: 'auto', display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', p: 4, position: 'relative', zIndex: 1 }}>
        <Zoom in={true} timeout={1000}>
          <Box sx={{ textAlign: 'center', mb: 4 }}>
            <Avatar sx={{ width: 100, height: 100, mx: 'auto', mb: 3, bgcolor: isDark ? '#2A2A2A' : 'white', border: '4px solid #9B59B6', boxShadow: '0 8px 32px rgba(155, 89, 182, 0.3)', animation: 'cat-bounce 2s infinite', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <LynaAvatar size={90} />
            </Avatar>
            <Typography variant="h3" className="cat-gradient-text" sx={{ fontWeight: 'bold', mb: 1, filter: isDark ? 'drop-shadow(0 2px 5px rgba(0,0,0,0.5))' : 'none' }}>Únete a Turn.ar</Typography>
            <Typography variant="body1" sx={{ color: taglineColor, mt: 1, fontStyle: 'italic', fontWeight: 'bold', letterSpacing: 0.5 }}>¡Empieza a organizar tu manada!</Typography>
          </Box>
        </Zoom>

        <Slide direction="up" in={showForm} timeout={800}>
          <Paper elevation={0} className="cat-card" sx={{ p: 4, width: '100%', bgcolor: cardBg, backdropFilter: 'blur(20px)', borderRadius: 4, border: `1px solid ${cardBorder}`, position: 'relative', overflow: 'hidden' }}>
            <Box sx={{ textAlign: 'center', mb: 3 }}>
              <Typography variant="h5" sx={{ fontWeight: 'bold', color: textPrimary, mb: 1 }}>Crear Cuenta Nueva ✨</Typography>
              <Typography variant="body2" color="text.secondary">Completa tus datos para comenzar</Typography>
            </Box>

            {error && <Fade in={true}><Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>{error}</Alert></Fade>}

            <Box component="form" onSubmit={handleSubmit} sx={{ width: '100%' }}>
              <TextField 
                fullWidth 
                label="Nombre Completo" 
                value={name} 
                onChange={(e) => setName(e.target.value)} 
                sx={{ mb: 2, '& .MuiOutlinedInput-root': { borderRadius: 3, bgcolor: isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.02)' } }} 
                InputProps={{ startAdornment: <Person sx={{ mr: 1, color: isTrippy ? 'secondary.main' : '#9B59B6' }} /> }} 
              />
              <TextField 
                fullWidth 
                label="Correo Electrónico" 
                value={email} 
                onChange={(e) => setEmail(e.target.value)} 
                sx={{ mb: 2, '& .MuiOutlinedInput-root': { borderRadius: 3, bgcolor: isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.02)' } }} 
                InputProps={{ startAdornment: <Email sx={{ mr: 1, color: isTrippy ? 'secondary.main' : '#9B59B6' }} /> }} 
              />
              <TextField 
                fullWidth 
                label="Contraseña" 
                type="password" 
                value={password} 
                onChange={(e) => setPassword(e.target.value)} 
                sx={{ mb: 3, '& .MuiOutlinedInput-root': { borderRadius: 3, bgcolor: isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.02)' } }} 
                InputProps={{ startAdornment: <VpnKey sx={{ mr: 1, color: isTrippy ? 'secondary.main' : '#9B59B6' }} /> }} 
              />
              <Button 
                type="submit" 
                fullWidth 
                variant="contained" 
                disabled={isLoading} 
                className="cat-button" 
                sx={{ 
                  py: 1.5, 
                  borderRadius: 3, 
                  fontSize: '1.1rem', 
                  fontWeight: 'bold', 
                  background: isTrippy ? `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)` : 'linear-gradient(135deg, #9B59B6 0%, #FF6B9D 100%)', 
                  textTransform: 'none',
                  boxShadow: '0 8px 20px rgba(155, 89, 182, 0.3)'
                }}
              >
                {isLoading ? 'Registrando...' : 'Crear Cuenta'}
              </Button>
              <Box sx={{ mt: 3, textAlign: 'center' }}>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>O regístrate con</Typography>
                <div ref={googleButtonRef} style={{ width: '100%', display: 'flex', justifyContent: 'center' }}></div>
              </Box>
              <Box sx={{ mt: 3, textAlign: 'center' }}>
                <Link to="/login" style={{ textDecoration: 'none' }}>
                  <Typography variant="body2" sx={{ color: linkColor, fontWeight: 'bold', '&:hover': { color: linkHoverColor, textDecoration: 'underline' } }}>¿Ya tienes cuenta? Inicia Sesión</Typography>
                </Link>
              </Box>
            </Box>
          </Paper>
        </Slide>
      </Box>
      <style>{` @keyframes float { 0%, 100% { transform: translateY(0px); } 50% { transform: translateY(-20px); } } `}</style>
    </Box>
  );
};

export default Register;